import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationBarProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  startIndex: number;
  itemsPerPage: number;
}

function getVisiblePages(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  pages.push(1);
  if (left > 2) pages.push('ellipsis');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('ellipsis');
  pages.push(total);

  return pages;
}

export default function PaginationBar({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  startIndex,
  itemsPerPage,
}: PaginationBarProps) {
  const [jumpValue, setJumpValue] = useState('');

  const handleJump = () => {
    const page = parseInt(jumpValue, 10);
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setJumpValue('');
    }
  };

  const showingText =
    totalItems === 0
      ? 'Showing 1 to 0 of 0 results'
      : `Showing ${startIndex + 1} to ${Math.min(startIndex + itemsPerPage, totalItems)} of ${totalItems} results`;

  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 px-6 py-4 shadow-lg">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700 font-medium">{showingText}</div>
        {totalPages > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-gray-300 hover:border-[#1875FF] hover:bg-[#1875FF]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              title="First page"
            >
              <ChevronsLeft size={16} />
            </button>
            <button
              onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-gray-300 hover:border-[#1875FF] hover:bg-[#1875FF]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              title="Previous page"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="flex items-center gap-1 mx-1">
              {visiblePages.map((page, idx) =>
                page === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} className="px-1 text-sm text-gray-400 select-none">
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`min-w-[32px] h-8 px-1.5 text-sm rounded-lg font-medium transition-all ${
                      page === currentPage
                        ? 'bg-[#1875FF] text-white shadow-sm'
                        : 'text-gray-700 hover:bg-[#1875FF]/10 hover:text-[#1875FF]'
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
            </div>

            <button
              onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-gray-300 hover:border-[#1875FF] hover:bg-[#1875FF]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              title="Next page"
            >
              <ChevronRight size={16} />
            </button>
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-gray-300 hover:border-[#1875FF] hover:bg-[#1875FF]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              title="Last page"
            >
              <ChevronsRight size={16} />
            </button>

            {totalPages > 7 && (
              <div className="flex items-center gap-1.5 ml-3 pl-3 border-l border-gray-200">
                <span className="text-xs text-gray-500 whitespace-nowrap">Go to</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={jumpValue}
                  onChange={(e) => setJumpValue(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                  placeholder="#"
                  className="w-12 h-8 px-2 text-sm text-center border border-gray-300 rounded-lg focus:outline-none focus:border-[#1875FF] focus:ring-1 focus:ring-[#1875FF]/30 transition-all"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
